<template>
  <div class="podcast-videos-page">
    <h2
      class="mt-0 tw-text-2xl tw-font-semibold tw-mb-2"
      style="color: var(--text-color)"
    >
      Videos
    </h2>
    <Card>
      <template #header>
        <div
          class="pt-4 px-4 flex flex-column md:flex-row md:items-center flex-wrap gap-3"
        >
          <Button
            label="Add Video"
            icon="pi pi-video"
            @click="openUploadDialog()"
          />
          <MultiSelect
            ref="showsFilterRef"
            v-model="selectedShowIds"
            :options="shows"
            option-label="show_title"
            option-value="show_id"
            placeholder="Filter by shows"
            class="w-full md:w-20rem"
            show-clear
            :show-toggle-all="false"
            :maxSelectedLabels="2"
            @change="applyFilters"
            :panelStyle="showsFilterPanelStyle"
          />
          <Calendar
            v-model="dateRange"
            selectionMode="range"
            :numberOfMonths="1"
            showIcon
            showButtonBar
            placeholder="Select date range"
            class="w-full md:w-auto"
            @update:modelValue="applyFilters"
          />
          <div class="duration-filter">
            <InputNumber
              v-model="minDuration"
              placeholder="Min sec"
              :useGrouping="false"
              class="duration-input"
              @update:modelValue="applyFilters"
            />
            <span class="duration-sep">–</span>
            <InputNumber
              v-model="maxDuration"
              placeholder="Max sec"
              :useGrouping="false"
              class="duration-input"
              @update:modelValue="applyFilters"
            />
          </div>
        </div>
      </template>
      <template #content>
        <div v-if="videoIdFilter" class="video-filter-banner">
          <span
            >Filtered by video: <b>{{ filteredVideoTitle }}</b></span
          >
          <i
            class="pi pi-times clear-icon"
            title="Clear filter"
            @click="clearVideoIdFilter"
          />
        </div>

        <div class="lib-header">
          <span class="lib-count"
            ><b>{{ totalVideos }}</b> videos</span
          >
          <Button
            type="button"
            icon="pi pi-refresh"
            @click="refreshVideos()"
            text
          />
        </div>

        <div v-if="loading" class="state-message">Loading…</div>
        <div v-else-if="!videos.length" class="state-message">
          No videos found.
        </div>
        <div v-else class="grid">
          <VideoCard
            v-for="video in videos"
            :key="video.video_id"
            :video="video"
            :id="video.video_id"
            :url="video.video_url"
            :thumbnail-url="video.thumbnail_url"
            :duration="video.duration"
            :width="video.width"
            :height="video.height"
            :created-at="video.created_at"
            :download-filename="`${video.title || video.video_id}.mp4`"
            :meta-lines="[
              { label: 'Show:', text: video.show_title },
              ...(video.podcaster_name
                ? [{ text: video.podcaster_name, muted: true }]
                : []),
            ]"
            :active-playing-id="currentlyPlayingId"
            :deleting-id="deletingVideoId"
            @play="setPlaying"
            @copy-link="copyLink"
            @delete="confirmDeleteVideo"
          />
        </div>

        <div class="paginator-row">
          <Paginator
            :rows="rows"
            :totalRecords="totalVideos"
            :first="firstRow"
            template="PrevPageLink PageLinks NextPageLink"
            @page="onPageChange"
          />
        </div>
      </template>
    </Card>

    <VideoUploadDialog
      v-model:visible="uploadDialogVisible"
      v-model:show-id="uploadShowId"
      :shows="shows"
      :shows-loading="showsLoading"
      :token="token"
      @uploaded="onVideoUploaded"
    />
  </div>
</template>

<script setup>
import Card from "primevue/card";
import MultiSelect from "primevue/multiselect";
import Paginator from "primevue/paginator";
import Button from "primevue/button";
import Calendar from "primevue/calendar";
import InputNumber from "primevue/inputnumber";
import { useConfirm } from "primevue/useconfirm";
import { computed, onBeforeMount, watch } from "vue";
import { useRoute } from "vue-router";
import { usePodcastVideos } from "./composables/usePodcastVideos";
import { useVideoUploadDialog } from "./composables/useVideoUploadDialog";
import { useMatchTriggerWidth } from "./composables/useMatchTriggerWidth";
import VideoUploadDialog from "./shared/VideoUploadDialog.vue";
import VideoCard from "@/components/shared/VideoCard.vue";

const confirm = useConfirm();
const route = useRoute();
const token = localStorage.getItem("podcastAdminToken");

const {
  shows,
  showsLoading,
  selectedShowIds,
  fetchShows,
  videos,
  totalVideos,
  rows,
  firstRow,
  loading,
  fetchVideos,
  refreshVideos,
  onPageChange,
  applyFilters,
  deletingVideoId,
  deleteVideo,
  currentlyPlayingId,
  setPlaying,
  copyLink,
  videoIdFilter,
  clearVideoIdFilter,
  minDuration,
  maxDuration,
  dateRange,
} = usePodcastVideos(token);

const { uploadDialogVisible, uploadShowId, openUploadDialog } =
  useVideoUploadDialog();

const { triggerEl: showsFilterRef, panelStyle: showsFilterPanelStyle } =
  useMatchTriggerWidth();

const onVideoUploaded = () => {
  refreshVideos();
};

const filteredVideoTitle = computed(() => {
  const video = videos.value.find((v) => v.video_id === videoIdFilter.value);
  return video?.title || video?.show_title || videoIdFilter.value;
});

const confirmDeleteVideo = (video) => {
  confirm.require({
    message: `Delete "${
      video.title || video.show_title
    }"? This cannot be undone.`,
    header: "Delete Video",
    icon: "pi pi-exclamation-triangle",
    acceptClass: "p-button-danger",
    accept: () => deleteVideo(video),
  });
};

const applyShowIdsFromQuery = (showIds) => {
  selectedShowIds.value = showIds
    ? String(showIds).split(",").filter(Boolean)
    : [];
};

onBeforeMount(() => {
  if (route.query.videoId) videoIdFilter.value = route.query.videoId;
  applyShowIdsFromQuery(route.query.showIds);
  fetchShows();
  fetchVideos();
});

watch(
  () => [route.query.videoId, route.query.showIds],
  ([videoId, showIds]) => {
    videoIdFilter.value = videoId || null;
    applyShowIdsFromQuery(showIds);
    applyFilters();
  }
);
</script>

<style scoped>
.duration-filter {
  display: flex;
  align-items: center;
  gap: 8px;
}

.duration-input :deep(.p-inputnumber-input) {
  width: 90px;
}

.duration-sep {
  color: var(--surface-400);
  font-weight: 600;
}

.video-filter-banner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  background: rgba(255, 90, 87, 0.1);
  border: 1px solid rgba(255, 90, 87, 0.3);
  border-left: 3px solid #ff5a57;
  border-radius: 8px;
  padding: 10px 14px;
  margin-bottom: 16px;
  font-size: 13px;
  color: var(--text-color);
}

.video-filter-banner .clear-icon {
  color: #ff5a57;
  cursor: pointer;
  font-size: 14px;
}

.video-filter-banner .clear-icon:hover {
  color: #d6423f;
}

.lib-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 14px;
}

.lib-count {
  font-size: 13px;
  color: var(--text-color-secondary);
}

.state-message {
  padding: 40px 0;
  text-align: center;
  color: var(--text-color-secondary);
}

.grid {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 14px;
}

@media (max-width: 1300px) {
  .grid {
    grid-template-columns: repeat(4, 1fr);
  }
}

@media (max-width: 1000px) {
  .grid {
    grid-template-columns: repeat(3, 1fr);
  }
}

@media (max-width: 600px) {
  .grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

.paginator-row {
  display: flex;
  justify-content: center;
  margin-top: 28px;
}

.paginator-row :deep(.p-paginator) {
  background: transparent;
  border: none;
}
</style>
